import {
  getEffectivePackage,
  type GetEffectivePackageParams,
  type ResolvedPackageDto,
} from '@/api/package-resolver';
import { retryUpTo } from '@/lib/query-retry';
import { useQuery } from '@tanstack/react-query';

/**
 * useEffectivePackage — E-105 Phase 2 S5 MVP.
 *
 * React Query hook: meghívja a GET /api/v1/package-resolver/effective
 * endpoint-ot és visszaadja az effective csomagot (vagy null-t).
 *
 * `enabled` automatikusan false ha productId nincs megadva.
 * Stale time 60 másodperc — a csomag ritkán változik intaking közben.
 */
export function useEffectivePackage(params: Partial<GetEffectivePackageParams>) {
  const enabled = !!params.productId;

  const query = useQuery({
    queryKey: [
      'package-resolver',
      'effective',
      params.productId,
      params.machineCategoryId ?? null,
      params.packageType ?? null,
    ],
    queryFn: async () => {
      if (!params.productId) return null;
      return getEffectivePackage({
        productId: params.productId,
        machineCategoryId: params.machineCategoryId,
        packageType: params.packageType,
      });
    },
    enabled,
    staleTime: 60_000,
    retry: retryUpTo(1),
  });

  return {
    effectivePackage: query.data as ResolvedPackageDto | null | undefined,
    isLoading: query.isLoading && enabled,
    error: query.error?.message ?? null,
  };
}
